import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import { FileText, AlertTriangle, CheckCircle2, HelpCircle, Loader2, RefreshCw, Terminal, Download, Wand2, Info, ExternalLink, ChevronRight, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';

// The Invoices portal. Files are ingested from the CLI, not uploaded here: a
// carrier file often needs a human to look at it first (a new layout, an
// unknown charge code), and an upload box invites loading something nobody has
// read. This page reports on what was ingested and what to chase.

// The simulator opens straight on the order, so "our figure" is always one
// click from the packing it came out of.
const simUrl = (n) => `/packing-simulator?order=${encodeURIComponent(String(n || '').replace('#', ''))}`;
const OrderCell = ({ number, tracking }) => (
  <>
    <a href={simUrl(number)} target="_blank" rel="noreferrer"
       className="font-mono hover:underline inline-flex items-center gap-1 group"
       title="Open this order in the packing simulator">
      {number}
      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-60 shrink-0" />
    </a>
    {tracking && <div className="text-[10px] text-muted-foreground font-mono">{tracking}</div>}
  </>
);

// Where it went. The line's own ISO code when the carrier states one (GLS
// always does, UPS not on every layout), otherwise the country from our
// record, which is a full name.
const Dest = ({ f }) => {
  const iso = (f.country || '').trim().toUpperCase();
  const full = (f.record_country || '').trim();
  if (!iso && !full) return <span className="text-muted-foreground">&mdash;</span>;
  return (
    <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-muted text-foreground/80 whitespace-nowrap"
          title={full || iso}>
      {iso || full}
    </span>
  );
};

// Whose kilo is whose. The single most misread number on an invoice: the
// carrier bills a chargeable weight, we compute one, and the real weight is
// usually neither.
const Weights = ({ f }) => (
  <div className="flex flex-col items-end gap-px leading-tight">
    <div className="whitespace-nowrap">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1.5">theirs</span>
      <span className="font-medium tabular-nums">{f.weight_billed != null ? `${f.weight_billed.toFixed(1)} kg` : '\u2014'}</span>
    </div>
    <div className="whitespace-nowrap">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1.5">ours</span>
      <span className={`tabular-nums ${f.geometry_exact === false ? 'text-muted-foreground italic' : ''}`}
            title={f.geometry_exact === false ? 'rebuilt from the item snapshot \u2014 a floor, not our real figure' : `${f.our_parcels || 0} parcel(s), chargeable`}>
        {f.our_kg != null ? `${f.our_kg.toFixed(1)} kg` : '\u2014'}
      </span>
    </div>
    {f.weight_actual ? (
      <div className="text-[10px] text-muted-foreground whitespace-nowrap tabular-nums">real {f.weight_actual.toFixed(1)} kg</div>
    ) : null}
    {f.billed_parcels > 1 || (f.our_parcels > 0 && f.our_parcels !== f.billed_parcels) ? (
      <div className={`text-[10px] whitespace-nowrap ${f.parcel_mismatch ? 'text-amber-700 dark:text-amber-400 font-medium' : 'text-muted-foreground'}`}>
        {f.billed_parcels} billed / {f.our_parcels ?? '\u2014'} ours
      </div>
    ) : null}
  </div>
);

// Each view is its own page now, so each says what it is for rather than
// repeating one description four times.
const VIEW = {
  activity: {
    title: 'Activity',
    blurb: 'Files arriving, lines being decided, and what each carrier\u2019s paperwork has taught us. '
         + 'The third stream is the one that compounds: every trap learned here is one the next invoice cannot spring.',
  },
  worklist: {
    title: 'To check',
    blurb: 'Every shipment worth chasing, across all carriers. Verdicts are recomputed on each load so they '
         + 'never go stale; only your decision is stored.',
  },
  invoices: {
    title: 'Invoices',
    blurb: 'Every file we have processed, what it reconciles to and how it compares with our own rates. '
         + 'Open one to go through its shipments.',
  },
  decisions: {
    title: 'Rate decisions',
    blurb: 'How we decided to model each carrier\u2019s rates, and why. Rate files arrive shaped the '
         + 'way the carrier bills, not the way Shippy prices, so every one of them forces choices '
         + 'that are invisible three months later unless they are written down. Read this before '
         + 'loading a new file; add to it when one teaches you something.',
  },
  carriers: {
    title: 'Carriers',
    blurb: 'What each carrier\u2019s paperwork is like before you open one of their files, and the traps '
         + 'already sprung on us.',
  },
};

const SEV = {
  check: { label: 'Check this', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', Icon: AlertTriangle },
  warn:  { label: 'Caveat',     cls: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300', Icon: HelpCircle },
  info:  { label: 'No check',   cls: 'bg-muted text-muted-foreground', Icon: HelpCircle },
  ok:    { label: 'Reconciles', cls: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300', Icon: CheckCircle2 },
};
const GOT = {
  high:   'border-red-300 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20',
  medium: 'border-amber-300 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20',
  low:    'border-border bg-muted/30',
};
const eur = (n) => (n == null ? '—' : `€${Number(n).toFixed(2)}`);

export default function CarrierInvoices() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(null);
  // The view is the ROUTE, not local state: these are menu items now, so each
  // one is linkable, bookmarkable and survives a refresh.
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const tab = pathname.endsWith('/worklist') ? 'worklist'
            : pathname.endsWith('/activity') ? 'activity'
            : pathname.endsWith('/carriers') ? 'carriers'
            : pathname.endsWith('/decisions') ? 'decisions'
            : 'invoices';
  const [carrier, setCarrier] = useState('');
  const [events, setEvents] = useState([]);
  const [ref, setRef] = useState(null);

  const load = () => {
    setLoading(true);
    axiosInstance.get('api/carrier-invoices/reference/')
      .then(r => setRef(r.data)).catch(() => {});
    axiosInstance.get('api/carrier-invoices/activity/')
      .then(r => setEvents(r.data?.events || [])).catch(() => {});
    axiosInstance.get('api/carrier-invoices/')
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.error || 'Failed to load invoices'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // A row is a shipment, so the decision lands on every line under it.
  const review = (f, status) => {
    setSaving(f.line_id);
    axiosInstance.post(`api/carrier-invoices/lines/${f.line_id}/review/`,
                       { review_status: status, line_ids: f.line_ids || [f.line_id] })
      .then(() => load())
      .catch(e => setError(e?.response?.data?.error || 'Could not save'))
      .finally(() => setSaving(null));
  };

  const download = (inv) => {
    axiosInstance.get(`api/carrier-invoices/${inv.id}/file/`, { responseType: 'blob' })
      .then(r => {
        const url = URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a');
        a.href = url; a.download = inv.source_filename || `${inv.carrier}_${inv.invoice_number}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      })
      .catch(() => setError('Could not download the file'));
  };

  const regenerate = (lineId, order) => {
    setSaving(lineId);
    axiosInstance.post(`api/carrier-invoices/lines/${lineId}/regenerate/`)
      .then(() => load())
      .catch(e => setError(e?.response?.data?.error || `Could not regenerate ${order}`))
      .finally(() => setSaving(null));
  };

  // Only the invoice views need the heavy payload. Activity and Carriers are
  // reference pages and render as soon as their own request lands.
  const heavy = tab === 'invoices' || tab === 'worklist';
  if (heavy && loading) return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading invoices…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">{error}</div>;

  const t = data?.totals || {};
  const invoices = data?.invoices || [];

  return (
    <div className="p-4 md:p-6 2xl:px-10">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground/70 mb-0.5">Invoice Analysis</div>
          <h1 className="text-2xl font-semibold tracking-tight">{VIEW[tab].title}</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{VIEW[tab].blurb}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
      </div>

      {(ref || data)?.basis && (
        <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3 text-xs flex gap-2">
          <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-px" />
          <div className="space-y-1">
            <div><span className="font-medium">Like for like.</span> Both sides of every comparison are <strong>shipping only</strong>: VAT arrives as its own line on the invoice, and box packaging is ours alone &mdash; the carrier never sees it. Fuel and carrier surcharges sit inside both figures.</div>
            <div className="text-muted-foreground">The stored solution total is shown separately as context, since it includes packaging and is priced on whichever carrier the engine chose.</div>
          </div>
        </div>
      )}

      {/* at a glance */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border rounded-lg overflow-hidden border border-border mt-5">
        {[['Invoices', t.invoices, ''], ['Freight', eur(t.freight_eur), ''],
          ['VAT & brokerage', eur(t.other_eur), ''],
          ['Reconcile', `${t.reconciled ?? 0} of ${t.freight_lines ?? 0}`, 'text-teal-700 dark:text-teal-400'],
          ['Need checking', t.to_check, t.to_check ? 'text-amber-700 dark:text-amber-400' : '']].map(([k, v, cls]) => (
          <div key={k} className="bg-card px-4 py-3">
            <div className={`text-xl font-semibold tabular-nums ${cls}`}>{v ?? '—'}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{k}</div>
          </div>
        ))}
      </div>

      {(data?.carriers || []).length > 1 && tab !== 'carriers' && tab !== 'activity' && (
        <div className="flex justify-end mt-4">
          <select className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                  value={carrier} onChange={e => setCarrier(e.target.value)}>
            <option value="">All carriers</option>
            {data.carriers.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {tab === 'activity' && (
        <div className="mt-6">
          {events.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
              Nothing yet.
            </div>
          ) : (
            <ol className="relative border-l border-border ml-3">
              {events.map((e, i) => {
                const meta = {
                  ingest:  ['bg-cool', 'text-blue-700 dark:text-blue-300', 'bg-blue-100 dark:bg-blue-900/30', FileText],
                  review:  ['', 'text-teal-700 dark:text-teal-300', 'bg-teal-100 dark:bg-teal-900/30', CheckCircle2],
                  learned: ['', 'text-amber-700 dark:text-amber-300', 'bg-amber-100 dark:bg-amber-900/30', Info],
                  decided: ['', 'text-indigo-700 dark:text-indigo-300', 'bg-indigo-100 dark:bg-indigo-900/30', Scale],
                  fixed:   ['', 'text-violet-700 dark:text-violet-300', 'bg-violet-100 dark:bg-violet-900/30', Wand2],
                }[e.kind] || ['', 'text-muted-foreground', 'bg-muted', Info];
                const EIcon = meta[3];
                return (
                  <li key={i} className="ml-6 pb-5">
                    <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-background ${meta[2]}`}>
                      <EIcon className={`h-3 w-3 ${meta[1]}`} />
                    </span>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-medium">{e.title}</span>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{e.carrier}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {new Date(e.at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                        {e.by ? ` · ${e.by}` : ''}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-3xl leading-relaxed">{e.detail}</p>
                    {(e.warnings || []).map((w, j) => (
                      <p key={j} className="text-xs text-amber-700 dark:text-amber-400 mt-1 flex gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />{w}
                      </p>
                    ))}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}

      {tab === 'worklist' && (
        <div className="mt-5">
          {(data?.worklist || []).filter(w => !carrier || w.carrier === carrier).length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Nothing flagged. Every line reconciles.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="text-left font-medium px-4 py-2">Order</th>
                    <th className="text-left font-medium px-3 py-2">To</th>
                    <th className="text-left font-medium px-3 py-2">Invoice</th>
                    <th className="text-right font-medium px-3 py-2">Invoiced</th>
                    <th className="text-right font-medium px-3 py-2">Chargeable kg</th>
                    <th className="text-right font-medium px-3 py-2">Ratio</th>
                    <th className="text-left font-medium px-3 py-2">What to check</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.worklist || []).filter(w => !carrier || w.carrier === carrier).map(w => {
                    const sv = SEV[w.severity] || SEV.info;
                    return (
                      <tr key={`${w.invoice_id}-${w.line_id}`}
                          className={`border-b border-border/60 last:border-0 align-top ${
                            w.severity === 'check' ? 'bg-amber-50/70 dark:bg-amber-950/20 border-l-[3px] border-l-amber-500' : ''}`}>
                        <td className="px-4 py-2.5 whitespace-nowrap"><OrderCell number={w.order_number} /></td>
                        <td className="px-3 py-2.5"><Dest f={w} /></td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{w.carrier} {w.invoice_number}<div className="text-[10px]">{w.invoice_date}</div></td>
                        <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{eur(w.all_in_eur ?? w.amount_eur)}</td>
                        <td className="px-3 py-2.5 text-xs"><Weights f={w} /></td>
                        <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                          {w.ratio ? <span className="font-semibold text-amber-700 dark:text-amber-400">{w.ratio}×</span> : '—'}
                        </td>
                        <td className="px-3 py-2.5 max-w-[540px]">
                          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold mr-1.5 ${sv.cls}`}>
                            <sv.Icon className="h-3 w-3" />{sv.label}
                          </span>
                          <span className="text-muted-foreground">{w.verdict}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'carriers' && (
        <div className="mt-5 space-y-4">
          {((ref || data)?.profiles || []).map(p => (
            <div key={p.carrier} className="rounded-lg border border-border bg-card overflow-hidden">
              <div className="px-4 py-2.5 border-b border-border font-semibold">{p.carrier}</div>
              <dl className="px-4 py-3 grid md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {[['File layouts', (p.formats || []).join(' · ')],
                  ['Order reference', p.reference],
                  ['Fuel', p.fuel],
                  ['VAT', p.vat],
                  ['Watch for', p.watch_for]].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</dt>
                    <dd className="mt-0.5 text-muted-foreground">{v}</dd>
                  </div>
                ))}
              </dl>
              <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                {Object.entries(p.charge_codes || {}).map(([code, meaning]) => (
                  <span key={code} title={meaning} className="inline-flex items-baseline gap-1.5 rounded border border-border bg-muted/40 px-2 py-1 text-xs">
                    <span className="font-mono font-medium">{code}</span>
                    <span className="text-muted-foreground">{meaning}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
          <h3 className="text-sm font-semibold pt-2">Gotchas</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {((ref || data)?.glossary || []).length > 0 && (
              <div className="mb-7">
                <h2 className="text-sm font-semibold mb-1">What every charge code means</h2>
                <p className="text-xs text-muted-foreground mb-3 max-w-3xl">
                  Carriers name most of their codes in their own files. What is added here is what
                  the name does not say: who the charge is for, whether we model it, and what to do
                  about it. These appear on the charge lines themselves inside an invoice.
                </p>
                <div className="rounded-lg border border-border bg-card divide-y divide-border">
                  {((ref || data)?.glossary || []).map((g, i) => (
                    <div key={i} className="px-4 py-2.5 flex gap-4 items-start">
                      <div className="w-40 shrink-0">
                        <span className="font-mono text-[11px] text-foreground">{g.code}</span>
                        <div className="text-[10px] text-muted-foreground">{g.carrier}</div>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{g.name}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{g.meaning}</p>
                        <p className={`text-[11px] mt-1 ${/^no/i.test(g.modelled)
                          ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground/80'}`}>
                          {g.modelled}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {((ref || data)?.gotchas || []).map((g, i) => (
              <div key={i} className={`rounded-lg border p-3.5 ${GOT[g.severity] || GOT.low}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-background/70 border border-border">{g.carrier}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{g.severity}</span>
                </div>
                <div className="font-medium text-sm">{g.title}</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{g.detail}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'decisions' && (
        <div className="mt-5 space-y-3">
          {((ref || data)?.decisions || []).length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
              Nothing logged yet.
            </div>
          ) : ((ref || data)?.decisions || []).map((d, i) => (
            <section key={i} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-baseline gap-2 flex-wrap mb-1.5">
                <h3 className="text-sm font-semibold">{d.title}</h3>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{d.carrier}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{d.area}</span>
                <span className="text-[11px] text-muted-foreground ml-auto">{d.at}</span>
              </div>
              <dl className="space-y-1.5 text-xs">
                <div className="flex gap-2">
                  <dt className="w-24 shrink-0 text-muted-foreground uppercase text-[10px] tracking-wide pt-0.5">Why</dt>
                  <dd className="text-muted-foreground leading-relaxed">{d.why}</dd>
                </div>
                {d.next && (
                  <div className="flex gap-2">
                    <dt className="w-24 shrink-0 text-muted-foreground uppercase text-[10px] tracking-wide pt-0.5">Next time</dt>
                    <dd className="leading-relaxed">{d.next}</dd>
                  </div>
                )}
                {d.cost && (
                  <div className="flex gap-2">
                    <dt className="w-24 shrink-0 text-amber-700 dark:text-amber-400 uppercase text-[10px] tracking-wide pt-0.5">What it costs</dt>
                    <dd className="text-amber-700 dark:text-amber-400 leading-relaxed">{d.cost}</dd>
                  </div>
                )}
              </dl>
            </section>
          ))}
        </div>
      )}

      {tab === 'invoices' && (
        invoices.length === 0 ? (
          <div className="mt-6 rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
            No invoices processed yet.
          </div>
        ) : (
          <div className="mt-5 rounded-lg border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-medium px-4 py-2">Invoice</th>
                  <th className="text-left font-medium px-3 py-2">Period</th>
                  <th className="text-right font-medium px-3 py-2">Total</th>
                  <th className="text-right font-medium px-3 py-2">Shipments</th>
                  <th className="text-right font-medium px-3 py-2" title="Shipments we could price on the carrier's own card">Priced</th>
                  <th className="text-right font-medium px-3 py-2" title="Our rates against what they charged, on the priced shipments">vs our rates</th>
                  <th className="text-left font-medium px-3 py-2">State</th>
                  <th className="text-right font-medium px-3 py-2">Loaded</th>
                </tr>
              </thead>
              <tbody>
                {invoices.filter(i => !carrier || i.carrier === carrier).map(inv => {
                  const sm = inv.summary || {};
                  return (
                    <tr key={inv.id}
                        onClick={() => navigate(`/carrier-invoices/files/${inv.id}`)}
                        className="border-b border-border/60 last:border-0 cursor-pointer hover:bg-muted/40 align-top">
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className="font-medium">{inv.carrier} {inv.invoice_number}</span>
                        <div className="text-[10px] text-muted-foreground font-mono">{inv.file_format} · {inv.line_count} lines</div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs text-muted-foreground">{inv.invoice_date}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                        {eur(inv.stated_total_eur)}
                        {Math.abs(inv.parsed_total_eur - inv.stated_total_eur) > 0.01 && (
                          <div className="text-[10px] text-amber-700 dark:text-amber-400">parsed {eur(inv.parsed_total_eur)}</div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{sm.shipments ?? inv.freight_line_count}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-muted-foreground">{sm.priced ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                        {sm.stale_card ? (
                          <span className="text-[11px] text-muted-foreground"
                                title="Billed before our current rate card came into force, so there is no card to price it against">
                            older tariff
                          </span>
                        ) : sm.delta_pct == null ? <span className="text-muted-foreground">—</span> : (
                          <>
                            <span className={sm.delta_eur > 0 ? 'text-amber-700 dark:text-amber-400 font-medium'
                                                              : 'text-teal-700 dark:text-teal-400 font-medium'}>
                              {sm.delta_pct > 0 ? '+' : ''}{sm.delta_pct}%
                            </span>
                            <div className="text-[10px] text-muted-foreground">
                              {eur(sm.our_card_eur)} vs {eur(sm.charged_eur)}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {sm.to_check > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                              <AlertTriangle className="h-3 w-3" />{sm.to_check}
                            </span>
                          )}
                          {sm.reconciled > 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                              <CheckCircle2 className="h-3 w-3" />{sm.reconciled}
                            </span>
                          )}
                          {sm.not_comparable > 0 && (
                            <span className="text-[11px] text-muted-foreground">{sm.not_comparable} n/a</span>
                          )}
                        </div>
                        {(inv.warnings || []).length > 0 && (
                          <div className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5">
                            {inv.warnings.length} parser note{inv.warnings.length === 1 ? '' : 's'}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap text-xs text-muted-foreground">
                        {new Date(inv.imported_at).toLocaleDateString()}
                        {inv.imported_by ? <div className="text-[10px]">by {inv.imported_by}</div> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      <div className="mt-8 rounded-lg border border-border bg-muted/30 p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <Terminal className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Adding a file</span>
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          Files are ingested from the command line, not uploaded here. A carrier file often needs a person to look at it
          first — a new layout, an unfamiliar charge code — and this page should report on what someone has read, not on
          whatever was dropped into a box.
        </p>
        <code className="block text-xs font-mono bg-background border border-border rounded px-2.5 py-1.5 overflow-x-auto">
          {data?.ingest_command}
        </code>
      </div>
    </div>
  );
}
